"use client";

import { Maximize2, Minimize2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useTheaterStateFor } from "@/components/virtualspace/virtual-space-theater-controller";
import {
  THEATER_ELIGIBLE_ATTR,
} from "@/lib/virtualspace/public-virtual-space-theater";

type Props = {
  /**
   * Stable key — the renderer passes `block.id`. Used as both the theater
   * controller's selection key (set on this element's data attribute) and
   * the rail's scroll target id.
   */
  blockKey: string;
  /** Optional accessible label for the inner toggle ("Theater mode for ..."). */
  label?: string;
  /**
   * Hide the in-corner T toggle (e.g. very small media that don't deserve
   * the focus). Theater mode auto-engages either way.
   */
  hideToggle?: boolean;
  /**
   * Extra classes on the outer wrapper. Theater width tier is applied via
   * `globals.css` based on the `data-vs-theater` state — callers don't need
   * to set max-width.
   */
  className?: string;
  children: ReactNode;
};

/**
 * Wrap any media block to opt it into theater mode. Provides:
 *  - The `data-vs-theater-eligible="true"` + `data-vs-theater-key={blockKey}`
 *    attrs the controller observes.
 *  - Default media width (`max-w-[min(1280px,100%)]`) which expands to
 *    `min(1600px, calc(100vw - 96px))` when this block is the active one.
 *  - In-corner Maximise / Minimise toggle for explicit theater pinning
 *    (mirrors YouTube's "T" key affordance — Esc exits).
 *  - Subtle `transition-[max-width]` so the width grow is calm, not janky;
 *    no transform/scale (would blur viewer canvases).
 *  - Reduced-motion respect via `motion-reduce:transition-none`.
 *
 * Post-353 most VirtualSpace renders skip this component because the
 * Space-row `<section>` itself carries the eligibility + theater-frame
 * styling. The toggle button has been extracted to
 * `<VirtualSpaceTheaterToggle>` so both paths can render the same
 * affordance without duplicating the JSX.
 */
export function VirtualSpaceMediaFrame({
  blockKey,
  label,
  hideToggle,
  className,
  children,
}: Props) {
  return (
    <div
      {...{ [THEATER_ELIGIBLE_ATTR]: "true" }}
      data-vs-theater-key={blockKey}
      className={cn("vs-theater-frame group/theater relative", className)}
    >
      {children}
      {!hideToggle ? (
        <VirtualSpaceTheaterToggle blockKey={blockKey} label={label} />
      ) : null}
    </div>
  );
}

/**
 * The corner Maximise / Minimise theater toggle — extracted so the
 * post-353 Space-row renderer can drop it inside a `<section>` that
 * already carries the eligibility attrs without nesting an extra
 * `<VirtualSpaceMediaFrame>` wrapper. Must be a child of an element
 * with the `group/theater` class so the hover-reveal affordance fires.
 */
export function VirtualSpaceTheaterToggle({
  blockKey,
  label,
}: {
  blockKey: string;
  label?: string;
}) {
  const { active, manual, toggle } = useTheaterStateFor(blockKey);
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={
        manual
          ? `Exit theater mode${label ? ` for ${label}` : ""} (Esc)`
          : `Enter theater mode${label ? ` for ${label}` : ""}`
      }
      aria-pressed={manual ? "true" : "false"}
      className={cn(
        "absolute right-3 top-3 z-30",
        "inline-flex h-9 w-9 items-center justify-center rounded-full",
        "border vs-border-soft bg-background/80 backdrop-blur-md",
        "text-muted-foreground/85 transition-opacity",
        "hover:text-foreground hover:bg-background",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--vs-accent-rgb))]",
        active
          ? "opacity-90"
          : "opacity-0 group-hover/theater:opacity-90 group-focus-within/theater:opacity-90",
      )}
    >
      {manual ? (
        <Minimize2 aria-hidden className="h-4 w-4" />
      ) : (
        <Maximize2 aria-hidden className="h-4 w-4" />
      )}
    </button>
  );
}
